import React, { useEffect } from "react";
import ToolbarHeader from "../../components/ToolbarHeader";
import CustomButton from "../../components/CustomButton";
import moment from "moment";
import DatePicker from "../../components/FormFields/DatePicker";
import { useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import SelectInput from "../../components/FormFields/SelectInput";
import InputWithIcon from "../../components/FormFields/InputWithIcon";
import { icons } from "../../assets/icons";
import DetailsCard from "../../components/DetailsCard";
import { FaChevronRight } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import RejectModal from "../../CustomRejectModal/RejectModal";
import { useMediaQuery } from "react-responsive";
import EditApproveModal from "../../CustomEditApproveModal/EditApproveModal";
import { displayDateFormat } from "../../utils/helpers";
import { getArrayOfSelectedStoresCode } from "../../../srcWeb/utils/helpers";
import { buildStandaloneDmsApprovePayload } from "../../../srcWeb/utils/dmsStandaloneListUtils";

const PettyCash = () => {
  const isMobileSmall = useMediaQuery({ query: "(max-width: 336px)" });

  const [selectedType, setSelectedType] = useState({});
  const { data = [], apiCall } = useApi();
  const {
    user = {},
    selectedStores,
    storeDropdownState,
    setStoreDropdownState,
  } = useAuth();
  const { storeCode = ""} = selectedStores?.storeDetails || {}
  const [isRejectVisible, setIsRejectVisible] = useState({
    isOpen: false,
    data: [],
  });
  const [isEditApproveVisible, setIsEditApproveVisible] = useState({
    isOpen: false,
    data: [],
  });
  const { data: data1, apiCall: apiCall1 } = useApi();
  const [startDate, setStartDate] = useState(
    moment().subtract({ days: 7 }).toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const navigate = useNavigate();
  const storeLogin = user?.userLabel == "Store";

  const normalizePettyCashListResponse = (rawResponse) => {
    const response = rawResponse || {};

    const docs = Array.isArray(response?.documents) ? response.documents : [];
    const storeWise = Array.isArray(response?.storeWiseRecordsResponses)
      ? response.storeWiseRecordsResponses
      : [];

    const normalizeApprovalStatusCode = (rawStatus) => {
      if (!rawStatus) return null;
      const s = String(rawStatus).trim();
      if (!s) return null;

      // If backend already returned the technical code, keep it as-is.
      const known = [
        "L1ApprovalPending",
        "L1Rejected",
        "L2ApprovalPending",
        "L2Rejected",
        "AccountsApprovalPending",
        "AccountsRejected",
        "Approved",
      ];
      if (known.includes(s)) return s;

      if (/^Approved$/i.test(s)) return "Approved";
      if (/^L1\s+Pending$/i.test(s)) return "L1ApprovalPending";
      if (/^L1\s+Rejected$/i.test(s)) return "L1Rejected";
      if (/^L2\s+Pending$/i.test(s)) return "L2ApprovalPending";
      if (/^L2\s+Rejected$/i.test(s)) return "L2Rejected";
      if (/^(Accounts|Finance)\s+Pending$/i.test(s) || /^L3\s+Pending$/i.test(s)) return "AccountsApprovalPending";
      if (/^(Accounts|Finance)\s+Rejected$/i.test(s) || /^L3\s+Rejected$/i.test(s)) return "AccountsRejected";

      // Handle variants like "L1 Approval Pending" (with spaces).
      if (/^L1\s*Approval\s*Pending$/i.test(s)) return "L1ApprovalPending";
      if (/^L2\s*Approval\s*Pending$/i.test(s)) return "L2ApprovalPending";
      if (/^Accounts\s*Approval\s*Pending$/i.test(s)) return "AccountsApprovalPending";

      return null;
    };

    const parseStoreCodeFromDescription = (text) => {
      if (text == null || String(text).trim() === "") return "";
      const s = String(text);
      const m1 = s.match(/Store\s*ID[-:\s]*([A-Za-z0-9]+)/i);
      if (m1) return m1[1].trim();
      const m2 = s.match(/\b(S\d{2,6})\b/);
      if (m2) return m2[1].trim();
      return "";
    };

    const resolveStoreIdentity = (doc, storeRow = null) => {
      const docStore = doc?.store || {};
      const rowStore = storeRow?.store || {};
      const attrs = Array.isArray(doc?.dataModalAttributeSet)
        ? doc.dataModalAttributeSet
        : [];
      const attrVal = (names) => {
        for (const n of names) {
          const a = attrs.find((x) => x?.metadataTechnicalName === n);
          const v = a?.attributeValue;
          if (v != null && String(v).trim() !== "") {
            return String(v).trim();
          }
        }
        return "";
      };
      const fromDescField =
        parseStoreCodeFromDescription(doc?.description) ||
        parseStoreCodeFromDescription(attrVal(["description"]));
      const storeCode =
        doc?.storeCode ||
        storeRow?.storeCode ||
        docStore?.storeCode ||
        docStore?.code ||
        rowStore?.storeCode ||
        rowStore?.code ||
        attrVal(["store_code", "storeCode", "STORE_CODE", "store"]) ||
        fromDescField ||
        "";
      const storeName =
        doc?.storeName ||
        storeRow?.storeName ||
        docStore?.name ||
        docStore?.storeName ||
        rowStore?.name ||
        rowStore?.storeName ||
        attrVal(["store_name", "storeName", "STORE_NAME"]) ||
        "";
      return { storeCode, storeName };
    };

    const normalizeFromDocuments = (doc) => {
      const attributes = Array.isArray(doc?.dataModalAttributeSet)
        ? doc.dataModalAttributeSet
        : [];
      const byTechnicalName = {};
      attributes.forEach((a) => {
        const key = a?.metadataTechnicalName;
        if (key) byTechnicalName[key] = a;
      });

      const getAttr = (technicalName) =>
        byTechnicalName?.[technicalName]?.attributeValue;

      const workflowSteps = Array.isArray(doc?.workflowStepResponses)
        ? doc.workflowStepResponses
        : [];
      const findStep = (label) =>
        workflowSteps.find((s) => s?.userLabel === label) || null;

      const l1 = findStep("L1");
      const l2 = findStep("L2");
      const financeStep = findStep("Finance");
      const { storeCode, storeName } = resolveStoreIdentity(doc);

      return {
        parentId: doc?.id,
        id: doc?.id,
        storeCode,
        storeName,
        date: getAttr("date") || null,
        description: getAttr("description") || "",
        amount: getAttr("amount") ?? 0,

        l1_amount_approved: getAttr("l1_amount_approved") ?? 0,
        l2_amount_approved: getAttr("l2_amount_approved") ?? 0,
        finance_amount_approved: getAttr("finance_amount_approved") ?? 0,

        L1: l1 ? { ...l1, status: l1?.status || l1?.action } : { status: "NA" },
        L2: l2 ? { ...l2, status: l2?.status || l2?.action } : { status: "NA" },
        Finance: financeStep
          ? {
              ...financeStep,
              userLabel: "Finance",
              status: financeStep?.status || financeStep?.action,
            }
          : { status: "NA" },
        // Used by the details header labelSplitFun().
        approval_status:
          normalizeApprovalStatusCode(getAttr("approval_status") || doc?.status) || null,
      };
    };

    if (docs.length > 0) {
      return docs
        .map(normalizeFromDocuments)
        .filter((x) => x?.parentId != null);
    }

    // Store branch: backend can return rows in documents or dataModalRecordsResponse
    return storeWise
      .flatMap((s) => {
        const childRows = Array.isArray(s?.documents)
          ? s.documents
          : Array.isArray(s?.dataModalRecordsResponse)
          ? s.dataModalRecordsResponse
          : [];
        return childRows.map((doc) => ({ doc, storeRow: s }));
      })
      .map(({ doc, storeRow }) => {
        const l1 = doc?.l1 || null;
        const l2 = doc?.l2 || null;
        const l3 = doc?.l3 || null;
        const finance = l3 || doc?.finance || null;
        const { storeCode, storeName } = resolveStoreIdentity(doc, storeRow);

        return {
          parentId: doc?.id,
          id: doc?.id,
          storeCode,
          storeName,
          date: doc?.submittedOn || null,
          description: doc?.description || "",
          amount: doc?.amount ?? 0,

          l1_amount_approved: l1?.approvedAmount ?? 0,
          l2_amount_approved: l2?.approvedAmount ?? 0,
          finance_amount_approved: finance?.approvedAmount ?? 0,

          L1: l1 ? { ...l1, status: l1?.status || l1?.action } : { status: "NA" },
          L2: l2 ? { ...l2, status: l2?.status || l2?.action } : { status: "NA" },
          Finance: finance
            ? { ...finance, userLabel: "Finance", status: finance?.status || finance?.action }
            : { status: "NA" },
          // Used by the details header labelSplitFun().
          approval_status:
            normalizeApprovalStatusCode(doc?.status) ||
            normalizeApprovalStatusCode(l1?.status ? `L1 ${l1.status}` : null) ||
            normalizeApprovalStatusCode(l2?.status ? `L2 ${l2.status}` : null) ||
            normalizeApprovalStatusCode(finance?.status ? `Accounts ${finance.status}` : null) ||
            null,
        };
      })
      .filter((x) => x?.parentId != null);
  };

  const onChangeType = (val, type) => {
    setSelectedType(val);
    

    // pettyCashTableDataApi(startDate, endDate, type);
  };

  // const isStoreActive = user.userLabel == "Store";


  useEffect(() => {
    // if(isStoreActive){
      pettyCashTableDataApi(startDate, endDate, selectedType?.value);
    // } else {
    //   if(storeCode.length > 0){
    //     pettyCashTableDataApi(startDate, endDate, selectedType?.value);
    //   }
    // }

  }, [selectedStores, selectedType, user]);

  

  const onClickRequest = () => {
    navigate("/requestimprest");
  };

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end !== null) pettyCashTableDataApi(start, end, selectedType?.value);
  };

  const pettyCashTableDataApi = (start, end, status) => {
    const nextSearch = createSearchParams({
      startDate: start,
      endDate: end,
    }).toString();
    const currentSearch = window.location.search?.startsWith("?")
      ? window.location.search.slice(1)
      : window.location.search || "";
    if (currentSearch !== nextSearch) {
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch,
        },
        { replace: true }
      );
    }
      apiCall({
        url: apiUrls.dmsService.getRecords,
        dataToSend: {
          startDate: moment(start).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
          metadataSchemaId: 6, // for Impreset Request metadataSchemaId is 6
          endDate: moment(end || start)
            .endOf("d")
            .format("YYYY-MM-DD HH:mm:ss"),
          searchText: "",
          status: status === "ALL_REQUEST" ? null : status,
          storeCodes: getArrayOfSelectedStoresCode(selectedStores, storeDropdownState)
          // storeCodes: selectedStores?.storeDetails?.storeCode
          //   ? selectedStores?.storeDetails?.storeCode !== "All" ? [selectedStores?.storeDetails?.storeCode] : null
          //   : user.userLabel == "Store" ? null : storeCode ? [storeCode] : [selectedStores[0]?.storeCode],
        },
        method: "post",
        processData: (raw) => normalizePettyCashListResponse(raw),
      });
  };

  const ImprestRequestApi = (payload, status) => {
    apiCall1({
      url: apiUrls.dmsService.approveRequest,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        toast.success(status);
        setIsRejectVisible({ ...isRejectVisible, isOpen: false });
        setIsEditApproveVisible({ ...isEditApproveVisible, isOpen: false });
        pettyCashTableDataApi(startDate, endDate, selectedType.value);
      },
    });
  };

  const approveRequestHandler = (ids = [], comment = "") => {
    ImprestRequestApi(
      buildStandaloneDmsApprovePayload({
        approved: ids,
        comment,
        rejected: [],
        saveDecisionOnly: false,
      }),
      "Approved Request Successfully"
    );
  };

  const rejectRequestHandler = (id = [], comment = "") => {
    ImprestRequestApi(
      buildStandaloneDmsApprovePayload({
        approved: [],
        comment,
        rejected: id,
        saveDecisionOnly: false,
      }),
      "Rejected Request Successfully"
    );
  };

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      Cell: (rowData) => {
        const id = rowData?.id;
        const allData = rowData;
        return (
          <div
            className="cursor-pointer"
            onClick={() => {
              navigate(`/petty-cash/${id}`, { state: allData });
            }}
          >
            {id}
          </div>
        );
      },
    },
    {
      Header: "Store Code",
      accessor: "date",
      Cell: (rowData) => {
        return (
          <div className="font-size-medium font-family-medium">
            {rowData?.storeCode ? String(rowData.storeCode) : "-"}
            {/* {rowData?.date} */}
          </div>
        );
      },
    },
    {
      Header: "Submit On",
      accessor: "date",
      Cell: (rowData) => {
        return (
          <div className="font-size-medium font-family-medium">
            {moment(rowData?.date).format(displayDateFormat)}
            {/* {rowData?.date} */}
          </div>
        );
      },
    },
    {
      Header: "Description",
      accessor: "description",
      Cell: (rowData) => {
        return (
          <div className="font-size-medium font-family-medium">
            {moment(rowData?.date).format(displayDateFormat)}
            {/* {rowData?.description} */}
          </div>
        );
      },
    },
    { 
      Header: "Amount (₹)", 
      accessor: "amount",
      Cell: (rowData) => {
        console.log("1234567890", rowData);
        if(user?.userLabel === "L1") {
          return rowData?.amount || ""
        } else if(user?.userLabel === "L2"){
          return rowData?.l1_amount_approved || ""
        } else if(user?.userLabel === "Finance") {
          return rowData?.l2_amount_approved || ""
        } else {
          const approvedAmount = rowData?.finance_amount_approved || rowData?.l2_amount_approved || 
          rowData?.l1_amount_approved || rowData?.amount || ""
          //   props?.row?.original?.Finance?.status === "Approved";
          // const value = props?.row?.original?.amount;
          return approvedAmount;
        }

        // const isFinanceApproved = rowData?.Finance?.status === "Approved";
        // const value = rowData?.amount;
        // return isFinanceApproved ? value || "-" : "-";
        // let rowDa = props?.row?.original || {}
        // const approvedAmount = rowData?.finance_amount_approved || rowData?.l2_amount_approved || 
        // rowData?.l1_amount_approved || "-"
        //   props?.row?.original?.Finance?.status === "Approved";
        // const value = props?.row?.original?.amount;
        // return approvedAmount;
      },
   },
    // {
    //   Header: "L1 Status",
    //   accessor: "l1_status",
    //   Cell: (rowData) => {
    //     const value = rowData?.L1?.status;
    //     if (!value) {
    //       return "-";
    //     }
    //     return (
    //       <div
    //         className={
    //           value === "Approved"
    //           ? "font-size-medium  text-[var(--successGreen)]"
    //           : value === "Rejected"
    //           ? "font-size-medium text-[var(--textRed)]"
    //           : "font-size-medium  text-[var(--pendingYellow)]"
    //         }
    //       >
    //         {value}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   Header: "L2 Status",
    //   accessor: "l2_status",
    //   Cell: (rowData) => {
    //     const value = rowData?.L2?.status;
    //     if (!value) {
    //       return "-";
    //     }
    //     return (
    //       <div
    //         className={
    //           value === "Approved"
    //           ? "font-size-medium  text-[var(--successGreen)]"
    //           : value === "Rejected"
    //           ? "font-size-medium text-[var(--textRed)]"
    //           : "font-size-medium  text-[var(--pendingYellow)]"
    //         }
    //       >
    //         {value}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   Header: "A/C Status",
    //   accessor: "ac_status",
    //   Cell: (rowData) => {
    //     const value = rowData?.Finance?.status;
    //     if (!value) {
    //       return "-";
    //     }
    //     return (
    //       <div
    //         className={
    //           value === "Approved"
    //             ? "font-size-medium  text-[var(--successGreen)]"
    //             : value === "Rejected"
    //             ? "font-size-medium text-[var(--textRed)]"
    //             : "font-size-medium  text-[var(--pendingYellow)]"
    //         }
    //       >
    //         {value}
    //       </div>
    //     );
    //   },
    // },
    {
      Header: "Approved Amount (₹)",
      accessor: "approved_amount",
      Cell: (rowData) => {
        let appvAmount;
        if(user?.userLabel === "L1") {
          appvAmount = rowData?.l1_amount_approved || ""
        } else if(user?.userLabel === "L2"){
          appvAmount = rowData?.l2_amount_approved || ""
        } else if(user?.userLabel === "Finance") {
          appvAmount = rowData?.finance_amount_approved || ""
        } else {
          appvAmount = rowData?.finance_amount_approved || rowData?.l2_amount_approved || 
          rowData?.l1_amount_approved || ""
          //   props?.row?.original?.Finance?.status === "Approved";
          // const value = props?.row?.original?.amount;
        }
        return appvAmount ? Number(appvAmount).toFixed(2) : "-"
        // const isFinanceApproved = rowData?.Finance?.status === "Approved";
        // const value = rowData?.amount;
        // return isFinanceApproved ? value || "-" : "-";
        // let rowDa = props?.row?.original || {}
        // const approvedAmount = rowData?.finance_amount_approved || rowData?.l2_amount_approved || 
        // rowData?.l1_amount_approved || "-"
        //   props?.row?.original?.Finance?.status === "Approved";
        // const value = props?.row?.original?.amount;
        // return approvedAmount;
      },
    },
  ];

  const approveRejectButtonStage = (allState = {}) => {
    let status = allState[user?.userLabel || ""]?.status || "";
    switch (status) {
      case "Rejected":
        return false;

      case "Approved":
        return false;

      case "Pending":
        return true;

      default:
        return false;
    }
  };

  console.log("- - - - --- - -- - data", storeDropdownState);
  return (
    <div className="dashboard-container">
      <ToolbarHeader
        title="Petty Cash"
        showBack={false}
        containerClassName="mt-14 text-[1rem]"
        textClassCss={isMobileSmall ? "text-[2.5rem]" : "text-[3rem]"}
        renderRight={() => {
          if (user?.userLabel == "Store") {
            return (
              <CustomButton
                title="Request Imprest Amount"
                wrapperClassName="!px-10"
                textClassName={
                  isMobileSmall ? "text-[1.3rem]" : "text-[1.5rem]"
                }
                onClick={onClickRequest}
              />
            );
          }
        }}
        showStores
      />

      <div className="flex flex-col gap-y-4 mb-4">
        {/* <SelectInput
          value={{name: storeDropdownState[0]?.name || "-", storeCode: storeDropdownState[0]?.storeCode}}
          valueField="storeCode"
          labelField="name"
          onChangeFullRes={true}
          options={storeDropdownState || []}
          onChange={(val) => {
            let data = val[0];
            pettyCashTableDataApi(startDate, endDate, 
              selectedType?.value === "ALL_REQUEST" ? null : 
              selectedType?.value || null
              );
            // onChangeType(data, 
            //   selectedType?.value === "ALL_REQUEST" ? null : 
            //   selectedType?.value || null
            //   )
          }}
        /> */}
        <SelectInput
          value={selectedType.key}
          valueField="value"
          labelField="key"
          onChangeFullRes={true}
          options={[
            { key: "All Request", value: "ALL_REQUEST" },
            { key: "Pending Request", value: "PENDING" },
            { key: "Approved Request", value: "APPROVED" },
          ]}
          onChange={(val) => {
            let data = val[0];
            if (data.value === "ALL_REQUEST") {
              onChangeType(data, null);
            } else if (data.value === "PENDING") {
              onChangeType(data, "PENDING");
            } else {
              onChangeType(data, "APPROVED");
            }
          }}
        />
        <DatePicker
          startDate={startDate}
          endDate={endDate}
          onChange={onChangeDate}
          maxDate={new Date()}
          dateRange
        />
        {/* <InputWithIcon
          placeholder="Search"
          containerClassName="search-input-wrapper !mt-0"
          inputClassName="font-size-small"
          rightIcon={icons.searchIcon}
        /> */}
      </div>
      <div className="overflow-auto relative" style={{ maxHeight: "68vh" }}>
        <DetailsCard
          showGlobalSearch
          data={data}
          rows={columns}
          isRejectApproveButtonEnable={
            selectedType?.value !== "APPROVED" && !storeLogin
          }
          approveRejectButtonStage={approveRejectButtonStage}
          sendRowDataInHandler
          rejectHandler={(rowData) =>
            setIsRejectVisible({ data: [rowData?.parentId], isOpen: true })
          }
          approveHandler={(rowData) => {
            const editedAmount = rowData?.finance_amount_approved ||
            rowData?.l2_amount_approved ||
              rowData?.l1_amount_approved ||
              rowData?.amount || 0;
            const ids = [
              { id: rowData?.parentId, approvedAmount: editedAmount },
            ];
            approveRequestHandler(ids);
          }}
          editApproveHandler={(rowData) => {
            const editedAmount = rowData?.finance_amount_approved ||
            rowData?.l2_amount_approved ||
              rowData?.l1_amount_approved ||
              rowData?.amount || 0;

            console.log(rowData, "rowData-", editedAmount);
            const ids = [
              { id: rowData?.parentId, approvedAmount: editedAmount, previousAm: editedAmount,  totalAmount: editedAmount },
            ];
            setIsEditApproveVisible({
              data: ids,
              isOpen: true,
              previousAmounts: ids,
            });
          }}
          customRow={(row, rowData) => {
            const id = rowData?.parentId;
            if (row.Header === "ID") {
              return (
                <div
                  className={`min-h-[5rem] bg-[var(--darkGray2)] flex items-center justify-between px-5 text-[var(--whiteColor)] rounded-tl-[1rem] rounded-tr-[1rem]`}
                  onClick={() => {
                    navigate(`/petty-cash/${id}`, { state: rowData });
                  }}
                >
                  <div className="min-w-[18rem] font-size-medium">
                    {/* {!storeLogin
                      ? storeDropdownState[0]?.name + ": " || ""
                      : ""} */}
                    {id}
                  </div>

                  <div className="flex gap-x-8">
                    <FaChevronRight color="var(--whiteColor)" size={15} />
                    {/* <img
                      src={icons.editLightIcon}
                      className="cursor-pointer"
                      alt="edit icon"
                    /> */}
                    {/* <img
                        src={icons.squareTickIcon}
                        className="cursor-pointer"
                        alt="approved icon"
                        onClick={() => approveRequestHandler(id)}
                      />
                      <img
                        src={icons.trashLightIcon}
                        className="cursor-pointer"
                        alt="trash icon"
                        onClick={() => rejectRequestHandler(id)}
                      /> */}
                  </div>
                </div>
              );
            }
          }}
        />
      </div>
      {isRejectVisible.isOpen && (
        <RejectModal
          isModalVisible={isRejectVisible}
          setIsModalVisible={setIsRejectVisible}
          rejectHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            rejectRequestHandler(validIds, comment);
          }}
          title="Imprest"
        />
      )}

      {isEditApproveVisible.isOpen && (
        <EditApproveModal
          isModalVisible={isEditApproveVisible}
          setIsModalVisible={setIsEditApproveVisible}
          approveHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            approveRequestHandler(validIds, comment);
          }}
          ids={isEditApproveVisible?.previousAmounts}
          totalAmount={isEditApproveVisible?.previousAmounts?.[0]?.totalAmount}
          title="Imprest"
        />
      )}
    </div>
  );
};

export default PettyCash;
